import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { SlideOver } from '@/components/ui/slide-over';
import { channelApi } from '@/api/channel.api';
import { create } from '@bufbuild/protobuf';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import {
  ChannelSessionSchema,
  ChannelP2PSessionSchema,
  ChannelSessionStateSchema,
} from '@/proto/agentplatform/v1/channel_pb';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LoaderIcon,
  PencilIcon,
  PlusIcon,
  RegenerateUuidIcon,
  TrashIcon,
} from '@/components/icons';
import { cn } from '@/lib/utils';

function formatTimestamp(ts: number | bigint | undefined) {
  if (ts == null || ts === 0n) return '-';
  const n = Number(ts);
  if (!n) return '-';
  return new Date(n / 1000000).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

type P2PRow = { localKey: string; id: string; name: string; idEditable: boolean };

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function randomUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function newRowKey(): string {
  return randomUUID();
}

function isValidUUID(s: string): boolean {
  return UUID_PATTERN.test(s.trim());
}

export default function ChannelSessionsPage() {
  const queryClient = useQueryClient();
  const [accountFilter, setAccountFilter] = useState('');
  const [userKeyword, setUserKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [p2pRows, setP2pRows] = useState<P2PRow[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);

  const listParams = useMemo(() => {
    const filter: Record<string, string> = {};
    const acc = accountFilter.trim();
    if (acc) {
      filter.account_id = acc;
    }
    const fuzzy: Record<string, string> = {};
    const kw = userKeyword.trim();
    if (kw) {
      fuzzy.channel_user_id = kw;
    }
    return create(ListRequestSchema, {
      page,
      pageSize,
      filter,
      fuzzy,
    });
  }, [page, pageSize, accountFilter, userKeyword]);

  const { data, isLoading } = useQuery({
    queryKey: ['channel_sessions', page, pageSize, accountFilter, userKeyword] as const,
    queryFn: () => channelApi.listChannelSessions(listParams),
  });

  const { data: editDetail, isLoading: editLoading } = useQuery({
    queryKey: ['channel_session', editId] as const,
    queryFn: () => channelApi.getChannelSession(editId!),
    enabled: drawerOpen && !!editId,
  });

  useEffect(() => {
    if (!drawerOpen || !editDetail) {
      return;
    }
    const p2p = editDetail.state?.p2p ?? [];
    setP2pRows(
      p2p.map((s, i) => ({
        localKey: `loaded-${i}-${s.id || newRowKey()}`,
        id: s.id,
        name: s.name,
        idEditable: false,
      }))
    );
    setCurrentIdx(
      Math.min(Math.max(0, editDetail.state?.currentIndex ?? 0), Math.max(0, p2p.length - 1))
    );
  }, [drawerOpen, editDetail]);

  const resetDrawer = () => {
    setDrawerOpen(false);
    setEditId(null);
    setP2pRows([]);
    setCurrentIdx(0);
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editId || !editDetail) {
        throw new Error('missing session');
      }
      if (p2pRows.length === 0) {
        throw new Error('至少保留一条会话');
      }
      if (currentIdx < 0 || currentIdx >= p2pRows.length) {
        throw new Error('当前会话无效，请在列表中重新选择「设为当前」');
      }
      for (const r of p2pRows) {
        const sid = r.id.trim();
        if (!sid) {
          throw new Error('请为新增会话填写会话 ID，或点击右侧图标生成 UUID');
        }
        if (!isValidUUID(sid)) {
          throw new Error(`会话 ID 须为 UUID：${sid}`);
        }
      }
      const state = create(ChannelSessionStateSchema, {
        p2p: p2pRows.map((r) =>
          create(ChannelP2PSessionSchema, { id: r.id.trim(), name: r.name.trim() || '未命名' })
        ),
        currentIndex: currentIdx,
      });
      const body = create(ChannelSessionSchema, {
        id: editDetail.id,
        tenantId: editDetail.tenantId,
        accountId: editDetail.accountId,
        channelUserId: editDetail.channelUserId,
        state,
        createdAt: editDetail.createdAt,
        updatedAt: editDetail.updatedAt,
        deletedAt: editDetail.deletedAt,
      });
      await channelApi.updateChannelSession(editId, body);
      return editId;
    },
    onSuccess: (savedId) => {
      queryClient.invalidateQueries({ queryKey: ['channel_sessions'] });
      queryClient.removeQueries({ queryKey: ['channel_session', savedId] });
      resetDrawer();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => channelApi.deleteChannelSession(id),
    onSuccess: () => {
      setDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ['channel_sessions'] });
    },
  });

  const rows = (data?.data ?? []).filter(Boolean);
  const totalCount = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const addP2pRow = () => {
    setP2pRows((prev) => {
      const next = [...prev, { localKey: newRowKey(), id: '', name: '', idEditable: true }];
      setCurrentIdx(prev.length);
      return next;
    });
  };

  const removeP2pRow = (index: number) => {
    setP2pRows((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      const next = prev.filter((_, i) => i !== index);
      setCurrentIdx((ci) => {
        let ni = ci;
        if (index === ci) {
          ni = Math.min(ci, next.length - 1);
        } else if (index < ci) {
          ni = ci - 1;
        }
        return Math.max(0, Math.min(ni, next.length - 1));
      });
      return next;
    });
  };

  return (
    <div className="mx-auto max-w-[1440px] space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1 min-w-0">
          <h2 className="text-2xl font-bold tracking-tight">会话管理</h2>
          <p className="text-sm text-muted-foreground">渠道用户 P2P 多会话状态</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="渠道账号 ID 精确筛选…"
            value={accountFilter}
            onChange={(e) => {
              setAccountFilter(e.target.value);
              setPage(1);
            }}
            className="h-9 w-44 sm:w-56"
          />
          <Input
            placeholder="渠道用户 ID 模糊搜索…"
            value={userKeyword}
            onChange={(e) => {
              setUserKeyword(e.target.value);
              setPage(1);
            }}
            className="h-9 w-44 sm:w-56"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card p-4 space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center rounded-xl border border-dashed border-border/80 bg-muted/20">
          <h3 className="text-base font-semibold mb-1">暂无会话记录</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            网关处理消息时会自动创建；也可稍后在渠道侧产生首条会话后刷新列表。
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border/60 bg-card shadow-sm">
          <Table className="min-w-[880px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b bg-muted/40">
                <TableHead className="text-xs sm:text-sm">会话 ID</TableHead>
                <TableHead className="text-xs sm:text-sm">渠道账号</TableHead>
                <TableHead className="text-xs sm:text-sm">渠道用户</TableHead>
                <TableHead className="text-xs sm:text-sm">会话数</TableHead>
                <TableHead className="text-xs sm:text-sm">当前会话</TableHead>
                <TableHead className="text-xs sm:text-sm">更新时间</TableHead>
                <TableHead className="text-right text-xs sm:text-sm w-[7rem]">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const p2p = row.state?.p2p ?? [];
                const p2pLen = p2p.length;
                const ci = row.state?.currentIndex;
                const idx = ci == null ? 0 : Number(ci);
                const current =
                  p2pLen > 0 && Number.isFinite(idx) && idx >= 0 && idx < p2pLen ? p2p[idx] : null;
                return (
                  <TableRow key={row.id} className="hover:bg-muted/50">
                    <TableCell className="font-mono text-xs max-w-[8rem] truncate" title={row.id}>
                      {row.id}
                    </TableCell>
                    <TableCell
                      className="font-mono text-xs max-w-[7rem] truncate"
                      title={row.accountId}
                    >
                      {row.accountId}
                    </TableCell>
                    <TableCell
                      className="font-mono text-xs max-w-[7rem] truncate"
                      title={row.channelUserId}
                    >
                      {row.channelUserId || '—'}
                    </TableCell>
                    <TableCell className="tabular-nums">{p2pLen}</TableCell>
                    <TableCell className="max-w-[14rem]">
                      {current ? (
                        <div
                          className="flex min-w-0 flex-col gap-0.5 text-xs"
                          title={`${current.name}\n${current.id}`}
                        >
                          <span className="truncate font-medium text-foreground">
                            {current.name || '—'}
                          </span>
                          <span className="truncate font-mono text-muted-foreground">
                            {current.id || '—'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                      {formatTimestamp(row.updatedAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="编辑状态"
                          onClick={() => {
                            setEditId(row.id);
                            setDrawerOpen(true);
                          }}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          title="删除"
                          onClick={() => setDeleteId(row.id)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell colSpan={7} className="bg-muted/30">
                  <div className="flex items-center justify-between py-1">
                    <span className="text-sm text-muted-foreground">
                      共 {totalCount} 条，第 {page} / {totalPages} 页
                    </span>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-muted-foreground">每页</span>
                        <select
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(1);
                          }}
                          className="h-7 rounded-md border border-input bg-background px-2 text-sm"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          上一页
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2"
                          disabled={page >= totalPages}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          下一页
                        </Button>
                      </div>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      <SlideOver
        open={drawerOpen}
        onOpenChange={(open) => {
          if (!open) {
            resetDrawer();
          }
        }}
        title="编辑渠道会话状态"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => resetDrawer()}>
              取消
            </Button>
            <Button
              size="sm"
              disabled={
                editLoading || !editDetail || p2pRows.length === 0 || updateMutation.isPending
              }
              onClick={() => updateMutation.mutate()}
            >
              {updateMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
              保存
            </Button>
          </div>
        }
      >
        {editLoading || !editDetail ? (
          <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
            <LoaderIcon className="h-5 w-5 animate-spin" />
            加载中…
          </div>
        ) : (
          <div className="space-y-4 max-w-lg">
            <div className="space-y-1 text-sm">
              <p>
                <span className="text-muted-foreground">会话 ID：</span>
                <span className="font-mono text-xs break-all">{editDetail.id}</span>
              </p>
              <p>
                <span className="text-muted-foreground">渠道账号：</span>
                <span className="font-mono text-xs">{editDetail.accountId}</span>
              </p>
              <p>
                <span className="text-muted-foreground">渠道用户：</span>
                <span className="font-mono text-xs break-all">{editDetail.channelUserId}</span>
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>会话列表</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1"
                  onClick={addP2pRow}
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  添加会话
                </Button>
              </div>
              <div className="rounded-md border max-h-72 overflow-y-auto divide-y divide-border">
                {p2pRows.map((r, idx) => (
                  <div
                    key={r.localKey}
                    className={cn(
                      'flex flex-col gap-2 p-3 bg-background sm:flex-row sm:flex-wrap sm:items-center',
                      idx === currentIdx && 'bg-primary/[0.06] ring-1 ring-inset ring-primary/25'
                    )}
                  >
                    <div className="flex shrink-0 items-center gap-2 min-w-0 sm:w-[7rem]">
                      {idx === currentIdx ? (
                        <span className="text-xs font-semibold text-primary">当前使用</span>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => setCurrentIdx(idx)}
                        >
                          设为当前
                        </Button>
                      )}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
                      <div className="flex min-w-0 flex-1 items-center gap-1.5">
                        {r.idEditable ? (
                          <>
                            <Input
                              className="h-8 min-w-0 font-mono text-xs"
                              value={r.id}
                              onChange={(e) => {
                                const v = e.target.value;
                                setP2pRows((prev) =>
                                  prev.map((x, i) => (i === idx ? { ...x, id: v } : x))
                                );
                              }}
                              placeholder="会话 ID（UUID）"
                              spellCheck={false}
                            />
                            <Button
                              type="button"
                              variant="secondary"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              title="随机生成 UUID"
                              onClick={() => {
                                const uid = randomUUID();
                                setP2pRows((prev) =>
                                  prev.map((x, i) => (i === idx ? { ...x, id: uid } : x))
                                );
                              }}
                            >
                              <RegenerateUuidIcon className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <div
                            className="flex h-8 min-w-0 flex-1 items-center rounded-md border border-input bg-muted/30 px-3 font-mono text-xs text-foreground"
                            title={r.id}
                          >
                            <span className="truncate">{r.id || '—'}</span>
                          </div>
                        )}
                      </div>
                      <Input
                        className="h-8 min-w-[8rem] flex-1 text-sm"
                        value={r.name}
                        onChange={(e) => {
                          const v = e.target.value;
                          setP2pRows((prev) =>
                            prev.map((x, i) => (i === idx ? { ...x, name: v } : x))
                          );
                        }}
                        placeholder="显示名称"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-destructive sm:ml-auto"
                        disabled={p2pRows.length <= 1}
                        onClick={() => removeP2pRow(idx)}
                        title="删除会话"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SlideOver>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除会话记录？</AlertDialogTitle>
            <AlertDialogDescription>
              将软删除该条 channel_sessions 并清除对应 Redis
              缓存；网关会按缺失会话重新创建默认状态。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
